import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { store } from '@store/index';
import { useAppDispatch, useAppSelector } from '@store/index';
import { validateSession, fetchCurrentUser, setDummyUser, selectIsAuthenticated } from '@features/auth/authSlice';
import { fetchCadStatus } from '@features/featureFlags/featureFlagsSlice';
import { selectTheme } from '@features/ui/uiSlice';
import { getTheme } from '@utils/theme';
import './i18n/config'; // Initialize i18n

// Pages
import LoginPage from '@pages/LoginPage';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';
import ResetPasswordPage from '@pages/ResetPasswordPage';
import DashboardPage from '@pages/DashboardPage';
import DocumentsPage from '@pages/DocumentsPage';
import DocumentDetailPage from '@pages/DocumentDetailPage';
import DocumentUploadPage from '@pages/DocumentUploadPage';
import SearchPage from '@pages/SearchPage';
import ProfilePage from '@pages/ProfilePage';
import UserSettingsPage from '@pages/UserSettingsPage';
import HelpPage from '@pages/HelpPage';
import AdminPage from '@pages/AdminPage';
import AuditDashboardPage from '@pages/AuditDashboardPage';
import AnomalyListPage from '@pages/AnomalyListPage';
import SettingsPage from '@pages/SettingsPage';
import NotFoundPage from '@pages/NotFoundPage';
import TestUploadPage from '@pages/TestUploadPage';
import DRBFMPage from '@pages/DRBFMPage';
import DRBFMHistoryPage from '@pages/DRBFMHistoryPage';
import VerificationPage from '@pages/VerificationPage';
import CADViewerPage from '@pages/CADViewerPage';

// Components
import PrivateRoute from '@components/auth/PrivateRoute';
import PermissionRoute from '@components/auth/PermissionRoute';
import MainLayout from '@components/layout/MainLayout';
import LoadingScreen from '@components/common/LoadingScreen';
import NotificationManager from '@components/common/NotificationManager';

import { useSessionManager } from './hooks/useSessionManager';

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectTheme);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [isInitializing, setIsInitializing] = React.useState(true);

  // Initialize session manager
  useSessionManager();

  useEffect(() => {
    const initialize = async () => {
      // Skip authentication check for public routes
      const publicPaths = ['/login', '/forgot-password', '/reset-password'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicPaths.some(path => currentPath.startsWith(path));

      if (isPublicRoute) {
        // Don't validate session on public routes to prevent redirect loop
        setIsInitializing(false);
        return;
      }

      try {
        // Validate session - HttpOnly cookie is sent automatically
        const isValid = await dispatch(validateSession()).unwrap();
        if (isValid) {
          await dispatch(fetchCurrentUser());
        }
      } catch (error) {
        console.error('Session initialization failed:', error);
        // In development, set a dummy user if session fails
        if (process.env.NODE_ENV === 'development') {
          console.log('Setting dummy user for development');
          await dispatch(setDummyUser());
        }
      }
      setIsInitializing(false);
    };

    initialize();
  }, [dispatch]);

  // Fetch CAD feature flag whenever auth state flips to authenticated. Covers
  // both initial session-restore and the login flow where the user enters via
  // `/login` (the initialize effect above early-returns for public routes,
  // and React Router navigation alone won't re-trigger it). The backend uses
  // the current user's tenant to resolve CAD_RESEARCH_TENANTS allowlist.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchCadStatus());
    }
  }, [dispatch, isAuthenticated]);

  if (isInitializing) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={getTheme(theme)}>
      <CssBaseline />
      <NotificationManager />
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Private routes */}
        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Document routes - temporarily remove permission checks for debugging */}
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/documents/upload" element={<DocumentUploadPage />} />
            <Route path="/documents/:id" element={<DocumentDetailPage />} />

            <Route path="/search" element={<SearchPage />} />
            <Route path="/drbfm" element={<DRBFMPage />} />
            <Route path="/drbfm/history" element={<DRBFMHistoryPage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/cad-viewer" element={<CADViewerPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<UserSettingsPage />} />
            <Route path="/help" element={<HelpPage />} />

            {/* /admin/activity (read-only summary + event list + CSV
                export) accepts manager OR admin server-side. /admin/anomalies
                stays admin-only because the resolve action is admin-only —
                gating it to manager would land managers on a page where
                their primary mutation action 403s (Codex P2 on PR #585).
                Declared BEFORE the /admin/* wildcard below so RR v6
                chooses the more specific match. */}
            <Route element={<PermissionRoute requiredRoles={['admin', 'manager']} />}>
              <Route path="/admin/activity" element={<AuditDashboardPage />} />
            </Route>

            {/* Admin-only routes (config / users / data mutations / anomaly triage) */}
            <Route element={<PermissionRoute requireAdmin={true} />}>
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/anomalies" element={<AnomalyListPage />} />
              <Route path="/admin/*" element={<AdminPage />} />
              <Route path="/test-upload" element={<TestUploadPage />} />
            </Route>
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <AppContent />
      </Router>
    </Provider>
  );
};

export default App;
